import { useEffect, useState } from 'react'
import { Grid } from 'semantic-ui-react'

import { Alert, Card, GridRowColumn, Text } from '@/componentsV1/BaseComponents'
import { TransactionHeader } from '@/features/transactions/lists/TransactionHeader'
import { TransactionCard } from '@/features/transactions/lists/TransactionCard'
import { TransactionCategoryIdentifier } from '@/reducers/admin/allTransactions.slice'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import DeprecatedTooltip from '@/features/taxes/quarterly/components/DeprecatedTooltip'
import './styles.scss'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectTransactionsByIds,
  selectUserTransactionsByCategoryIdentifierForYear,
} from '@/features/transactions/transactions.selectors'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

interface IOwnerInvestmentProps {
  setIsStepComplete?: (value: boolean) => void
}

export const OwnersInvestment = ({
  setIsStepComplete,
}: IOwnerInvestmentProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)

  const investmentTransactions = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.owners_investments,
    taxYear
  )

  const [investmentTransactionsIds, setInvestmentTransactionIds] = useState<
    number[]
  >([])

  useEffect(() => {
    if (investmentTransactions.length && !investmentTransactionsIds.length) {
      setInvestmentTransactionIds(investmentTransactions.map((t) => t.id))
    }
  }, [investmentTransactionsIds.length, investmentTransactions])

  const localTransactions = useReselector(
    selectTransactionsByIds,
    investmentTransactionsIds
  )

  useEffect(() => {
    const transactionsCategorized = localTransactions.every(
      (t) =>
        t.type === TransactionRuleCategoryType.personal ||
        (t.type === TransactionRuleCategoryType.business &&
          t.transactionCategory) ||
        t.notes
    )

    setIsStepComplete?.(transactionsCategorized)
  }, [localTransactions, setIsStepComplete])

  return (
    <Grid>
      <GridRowColumn centerContent className="header-container">
        <StepHeaders
          imageSrc="https://heard-images.s3.amazonaws.com/assets/taxesV2.svg"
          imageAlt="taxes"
          imageWidth={180}
          imageHeight={180}
          title="Do these Owner’s Investments look correct?"
          description=""
          customColor="green"
        >
          <Grid>
            <GridRowColumn className="align-left">
              <Text>
                We categorized the following transactions as{' '}
                <b>Owner’s Investments</b>, or money you put into your business.
                If something doesn’t look right, please add a note to explain
                why.
              </Text>
            </GridRowColumn>
            <GridRowColumn width={12} className="tooltip-grid">
              <DeprecatedTooltip
                popup={{
                  title: 'Owner’s Investments',
                  body: 'Money or assets that an owner contributes towards starting or running a business.<br/>An example of this is income that was originally paid into your personal account, but transferred back into your business account.',
                }}
                labelComponent="What is an Owner’s Investment?"
              />
            </GridRowColumn>
          </Grid>
        </StepHeaders>
      </GridRowColumn>
      {investmentTransactionsIds?.length > 0 && <TransactionHeader />}
      <GridRowColumn short>
        {investmentTransactionsIds.map((tId) => (
          <TransactionCard key={tId} transactionId={tId} />
        ))}
      </GridRowColumn>
      {investmentTransactionsIds?.length === 0 && (
        <GridRowColumn width={10} spacer={3}>
          <Card backgroundColor="lightGreen">
            <Text>
              All transactions have been recategorized. You may continue to the
              next step.
            </Text>
          </Card>
        </GridRowColumn>
      )}
      <GridRowColumn width={10} spacer={3} className="alert-footer">
        <Alert title="Why are we asking this?">
          Sometimes business income is miscategorized as Owner’s Investments. We
          need you to confirm if the above transactions are truly Owner’s
          Investments.
        </Alert>
      </GridRowColumn>
    </Grid>
  )
}
